/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Grid, Popover } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronUpIcon } from '@coursera/cds-icons';

import ChipButton from 'bundles/search-common/components/experiments/filters2024/ChipButton';

import _t from 'i18n!nls/search-common';

const styles = {
  resetButtonStyles: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
  `,
  applyButtonStyles: css`
    border-radius: var(--cds-spacing-100);
  `,
  dropdownStyles: css`
    .cds-popoverDropdown-paper {
      border-radius: var(--cds-spacing-200);
      border-color: var(--cds-color-neutral-background-primary);
      box-shadow: 0 4px 12px 4px var(--cds-color-darken-200);
      max-width: 420px;
    }

    .cds-popoverFooter-root {
      border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
};

export type FilterChipDropdownProps = {
  triggerPrefix?: React.ReactNode;
  triggerCount?: number;
  triggerLabel?: string;
  panelHeader?: React.ReactNode;
  children: React.ReactNode;
  onApply?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onReset?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onClose?: () => void;
  shouldDisableCta?: boolean;
  onTriggerClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  shouldAutoApply?: boolean; // Keep popover open when auto-apply is enabled
};

export const FilterChipDropdown = ({
  triggerPrefix,
  triggerLabel = '',
  triggerCount = 0,
  panelHeader,
  onApply,
  onReset,
  onClose,
  onTriggerClick,
  children,
  shouldDisableCta,
  shouldAutoApply = false,
}: FilterChipDropdownProps): React.ReactElement => {
  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(popoverAnchor);

  return (
    <>
      <ChipButton
        onClick={(e) => {
          if (onTriggerClick) {
            onTriggerClick(e);
          } else {
            setPopoverAnchor(e.currentTarget);
          }
        }}
        prefix={triggerPrefix}
        suffix={isOpen ? <ChevronUpIcon size="small" /> : <ChevronDownIcon size="small" />}
        isActive={triggerCount > 0}
        count={triggerCount}
        showCount={triggerCount > 2}
      >
        {triggerLabel}
      </ChipButton>
      <Popover
        open={isOpen}
        anchorElement={popoverAnchor}
        onClose={() => {
          onClose?.();
          setPopoverAnchor(null);
        }}
        dropdownProps={{
          css: styles.dropdownStyles,
        }}
      >
        <Popover.Header>{panelHeader}</Popover.Header>
        <Popover.Body component="div" tabIndex={0}>
          {children}
        </Popover.Body>
        <Popover.Footer>
          <Grid container>
            <Grid item>
              <Button
                css={styles.applyButtonStyles}
                variant="primary"
                type="button"
                size="small"
                onClick={(e) => {
                  onApply?.(e);
                  setPopoverAnchor(null);
                }}
              >
                {_t('View')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                css={styles.resetButtonStyles}
                disabled={shouldDisableCta}
                variant="ghost"
                size="small"
                onClick={(e) => {
                  onReset?.(e);
                }}
              >
                {_t('Clear All')}
              </Button>
            </Grid>
          </Grid>
        </Popover.Footer>
      </Popover>
    </>
  );
};

export default FilterChipDropdown;
